import React, { useContext } from 'react';
import Dialog from '../../../../../BaseComponents/Dialog/EDDialog';
import {
  Theme,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import { AvenyThemeDark } from '../../../../../BaseComponents/ThemeProvider/AvenyTheme';
import { IUIPage } from '../../../../../Types/UrvalTypes';
import {
  FieldPickerItemType,
  UrvalFieldSortModeType,
} from '../Types/UrvalFieldPickerTypes';
import SearchBar from '../../../../SearchBar/Components/SearchBar/SearchBar';
import memoizeOne from 'memoize-one';
import {
  getFieldsMultipleLevels,
  IUrvalPickerData,
  addFieldPickerItemDuplicate,
  addNewPage,
  removePage,
  getFieldPickerItems,
  mergeFieldPickerItems,
  filterFieldsVirtualTree,
  setOpen,
  setLevel,
} from '../Utils/UrvalFieldsUtils';
import {
  IRutinSettingsContext,
  RutinSettingsContext,
} from '../../../../../Features/Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import {
  AvenyCollectionMap,
  ModelContextConsumer,
} from '../../../../../Model/ModelContext/ModelContext';
import { createPageFromFieldPickerItems } from '../Utils/FieldPickerItemsConvertUtils';
import {
  findNodePathById,
  ICheckedItems,
  ITreeSection,
  SectionCheckStatus,
} from '../../../../../BaseComponents/TreeSelect/TreeSelectUtils';
import EDIconButton from '../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDIcon from '../../../../../BaseComponents/Icon/EDIcon';
import {
  getDefaultConditionObject,
  getDefaultExtendedConditionObject,
} from '../Utils/UrvalConditionUtils';
import debounce from 'debounce';
import { assocPath } from 'ramda';
import EDTreeSelect from '../../../../../BaseComponents/TreeSelect/EDTreeSelect';

interface IProps {
  collectionName: string;
  componentName?: string;
  page: IUIPage;
  setPage: (newUrvalPage: IUIPage) => void;
  open: boolean;
  onClose: () => void;
  openWithAllExpandableOpen?: boolean;
  fieldSortMode?: UrvalFieldSortModeType;
  disableVirtualTreeSelect?: boolean;
  classes?: any;
}

interface IInnerProps extends IProps {
  model: AvenyCollectionMap;
  rutinAvailabilityContext: IRutinSettingsContext;
}

interface IState {
  searchValue: string;
  /** Mirrors props.open, used to reset searchValue when opening dialog */
  open: boolean;
  items: ITreeSection<IUrvalPickerData>[];
  filteredItems: ITreeSection<IUrvalPickerData>[];
}

const styles = (theme: Theme) => ({
  headerTopArea: {
    alignItems: 'flex-end',
  },
  headerTools: {
    width: '70%',
  },
  newFieldButton: {
    marginRight: theme.spacing(3),
    '& > *': {
      marginLeft: theme.spacing(2),
    },
  },
});

export type SetFieldPickerItemsType = (
  newFieldPickerItems: FieldPickerItemType[]
) => void;

const findNodeById = (
  sections: ITreeSection<IUrvalPickerData>[],
  id: string
): ITreeSection<IUrvalPickerData> | null => {
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];

    if (section.id === id) {
      return section;
    } else if (section.subList && section.subList.length > 0) {
      const maybeNode = findNodeById(section.subList, id);
      if (maybeNode) {
        return maybeNode;
      }
    }
  }

  return null;
};

let worker: Worker | undefined;
if (window.Worker) {
  worker = new Worker(
    new URL('./getFieldPickerItems.worker.ts', import.meta.url),
    {
      type: 'module',
    }
  );
}

class UrvalFieldPickerDialog extends React.Component<IInnerProps, IState> {
  static getDerivedStateFromProps(nextProps: IInnerProps, prevState: IState) {
    if (prevState.open !== nextProps.open) {
      if (nextProps.open === true) {
        const items = getFieldPickerItems(
          {
            page: nextProps.page,
            fieldSortMode: nextProps.fieldSortMode,
            collectionName: nextProps.collectionName,
            componentName: nextProps.componentName,
            openWithAllExpandableOpen: nextProps.openWithAllExpandableOpen,
            rutinId:
              nextProps.rutinAvailabilityContext.activeRoutine?.routine.rutinID,
            model: nextProps.model,
          },
          0
        );

        return {
          items,
          open: nextProps.open,
          searchValue: '',
        };
      } else {
        return {
          open: nextProps.open,
        };
      }
    }

    return null;
  }

  memoizedGetAllFields = memoizeOne(getFieldsMultipleLevels);

  constructor(props: IInnerProps) {
    super(props);

    const items = props.open
      ? getFieldPickerItems(
          {
            page: props.page,
            fieldSortMode: props.fieldSortMode,
            collectionName: props.collectionName,
            componentName: props.componentName,
            openWithAllExpandableOpen: props.openWithAllExpandableOpen,
            rutinId:
              props.rutinAvailabilityContext.activeRoutine?.routine.rutinID,
            model: props.model,
          },
          worker ? 0 : 3
        )
      : [];

    if (worker) {
      worker.onmessage = message => {
        this.setState(prevState => {
          const nextLevel = 4;
          if (message.data.levels < nextLevel) {
            this.loadFields(nextLevel);
          }

          const mergedItems = mergeFieldPickerItems(
            prevState.items,
            message.data.items
          );

          return {
            items: setLevel(
              setOpen(mergedItems, Boolean(prevState.searchValue), true)
            ),
          };
        });
      };
    }

    if (props.open) {
      this.loadFields(2);
    }

    this.state = {
      searchValue: '',
      open: props.open,
      items: items,
      filteredItems: items,
    };
  }

  componentDidUpdate(prevProps: IInnerProps, prevState: IState) {
    if (prevProps.open !== this.props.open && this.props.open) {
      this.loadFields(2);
    }

    if (prevState.items !== this.state.items) {
      const filteredItems = filterFieldsVirtualTree(
        this.state.items,
        this.state.searchValue,
        false
      );

      this.setState({ filteredItems });
    }
  }

  setFieldPickerItems = (sections: ITreeSection<IUrvalPickerData>[]) => {
    this.setState({
      items: sections,
    });
  };

  loadFields = (levels: number) => {
    if (worker) {
      worker.postMessage({
        props: {
          page: this.props.page,
          fieldSortMode: this.props.fieldSortMode,
          collectionName: this.props.collectionName,
          componentName: this.props.componentName,
          model: this.props.model,
          rutinId:
            this.props.rutinAvailabilityContext.activeRoutine?.routine.rutinID,
        },
        levels: levels,
      });
    }
  };

  filterArboristData = (): void => {
    this.setState({
      items: setLevel(
        setOpen(this.state.items, Boolean(this.state.searchValue))
      ),
    });
  };

  debouncedFilterArboristData = debounce(this.filterArboristData, 200);

  onSearchValueChange = (newSearchValue: string) => {
    this.setState({
      searchValue: newSearchValue,
    });
    this.debouncedFilterArboristData();
  };

  onCommit = () => {
    const newPage = createPageFromFieldPickerItems(
      this.state.items,
      this.props.page
    );

    this.props.setPage(newPage);
    this.props.onClose();
  };

  onClose = () => {
    this.props.onClose();
  };

  isChecked = (section: ITreeSection<IUrvalPickerData>) => {
    return section.data?.conditionObject
      ? SectionCheckStatus.FullyChecked
      : SectionCheckStatus.NotChecked;
  };

  onNewPageClick =
    (section: ITreeSection<IUrvalPickerData>) =>
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      const node = findNodeById(this.state.items, section.id);

      if (node) {
        const newSections = addNewPage(node, this.state.items);

        this.setFieldPickerItems([...newSections]);
      }
    };

  onRemoveInPageClick =
    (section: ITreeSection<IUrvalPickerData>) =>
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      const node = findNodeById(this.state.items, section.id);
      if (node && node.parentNode) {
        const newSections = removePage(node.parentNode, node, this.state.items);

        this.setFieldPickerItems([...newSections]);
      }
    };

  renderListItemButtons = (
    section: ITreeSection<IUrvalPickerData>,
    parent?: ITreeSection<IUrvalPickerData>
  ) => {
    return (
      <div className={this.props.classes.newFieldButton}>
        {section.subList && section.checked && (
          <EDIconButton
            tooltip="Ny sida"
            icon={<EDIcon color="inherit" iconType="note_add" />}
            onClick={this.onNewPageClick(section)}
            padding="none"
            margin="none"
          />
        )}
        {section.data?.type === 'page' && parent && (
          <EDIconButton
            tooltip="Ta bort sida"
            icon={<EDIcon color="inherit" iconType="remove_circle" />}
            onClick={this.onRemoveInPageClick(section)}
            padding="none"
            margin="none"
          />
        )}
        {section.checked && !section.duplicateNr && (
          <EDIconButton
            tooltip="Nytt fält"
            icon={<EDIcon color="inherit" iconType="add_circle" />}
            onClick={this.addButtonClick(section)}
            padding="none"
            margin="none"
          />
        )}
      </div>
    );
  };

  showListItemButtons = (section: ITreeSection<IUrvalPickerData>) => {
    return Boolean(
      (section.data?.conditionObject && section.checked) ||
        section.data?.type === 'page'
    );
  };

  onCheck = (
    sections: ITreeSection<IUrvalPickerData>[],
    checkedItems: ICheckedItems<IUrvalPickerData>,
    checked: boolean,
    suggestion?: ITreeSection<IUrvalPickerData>
  ): ITreeSection<IUrvalPickerData>[] => {
    if (!suggestion) {
      return sections;
    }
    const section = checkedItems.checkedItem;
    let items = sections;

    const itemPath: Array<string | number> = [];
    const item = findNodePathById(sections, suggestion.id, itemPath);

    if (item) {
      item.checked = checked;
      if (item.data?.attribute) {
        item.data = {
          ...item.data,
          conditionObject: checked
            ? getDefaultConditionObject(
                item.data.attribute,
                item.data?.modelPath,
                item.data?.typeTree
              )
            : undefined,
        };
        items = assocPath(itemPath, item, items);
        if (
          suggestion &&
          !section.data?.conditionObject &&
          section.data?.association
        ) {
          section.data = {
            ...section.data,
            conditionObject: checked
              ? getDefaultExtendedConditionObject(
                  this.props.model,
                  section.data?.association,
                  section.data?.modelPath
                )
              : undefined,
          };
        }
        return items;
      }
    }

    return sections;
  };

  addButtonClick =
    (section: ITreeSection<IUrvalPickerData>) => (event: React.MouseEvent) => {
      event.stopPropagation();
      const node = findNodeById(this.state.items, section.id);
      if (node) {
        const parentNode = node.parentNode
          ? findNodeById(this.state.items, node?.parentNode.id)
          : undefined;
        const newItems = addFieldPickerItemDuplicate(
          parentNode ?? node,
          this.state.items,
          parentNode ? node : undefined
        );
        this.setFieldPickerItems([...newItems]);
      }
    };

  disableSectionsClick = (section: ITreeSection<IUrvalPickerData>) => {
    return true;
  };

  render() {
    const { onClose, open } = this.props;
    const { searchValue } = this.state;

    return (
      <Dialog
        open={open}
        title={'Välj fält'}
        onClose={this.onClose}
        footerActions={[
          { onClick: onClose, label: 'Avbryt' },
          {
            onClick: this.onCommit,
            label: 'Använd',
          },
        ]}
        fullWidth={true}
        fullHeight
        headerTools={
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={AvenyThemeDark}>
              <SearchBar
                value={searchValue}
                onChange={this.onSearchValueChange}
              />
            </ThemeProvider>
          </StyledEngineProvider>
        }
        headerProps={{
          classes: {
            content: '', // FIXME: alla classes borde inte vara required, är pga IBaseprops men denna körs inte genom withStyles eftersom den inte skickas direkt till en komponent
            root: '',
            bottomArea: '',
            subtitle: '',
            title: '',
            cursor: '',
            topArea: this.props.classes.headerTopArea,
            tools: this.props.classes.headerTools,
            topToolbar: '',
            titleContainer: '',
          },
        }}
        disableContentWrapperMargin
      >
        <EDTreeSelect
          virtual={!this.props.disableVirtualTreeSelect}
          filteredSections={this.state.filteredItems}
          onCheck={this.onCheck}
          searchValue={searchValue}
          renderListItemButtons={this.renderListItemButtons}
          showListItemButtons={this.showListItemButtons}
          sectionsUpdate={this.setFieldPickerItems}
          disableSectionsClick={this.disableSectionsClick}
          sections={this.state.items}
        />
      </Dialog>
    );
  }
}

const EnhancedUrvalFieldPickerDialog: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const rutinAvailabilityContext = useContext(RutinSettingsContext);

  return (
    <ModelContextConsumer>
      {({ avenyCollectionMap }) => (
        <UrvalFieldPickerDialog
          model={avenyCollectionMap}
          rutinAvailabilityContext={rutinAvailabilityContext}
          {...props}
        />
      )}
    </ModelContextConsumer>
  );
};

export default withStyles(EnhancedUrvalFieldPickerDialog, styles);
